/**
 * The simulation clock: one fixed step, owned in one place.
 *
 * The design fixes the sim at 20 Hz (`docs/GAME-DESIGN.md`, `TODO.md` #45) and
 * every rule that advances over time — movement, cooldowns, durations — is
 * written against that step. The rate therefore cannot live in the Colyseus room
 * *and* in the client's prediction loop: two copies of a constant are two
 * constants, and the day one of them changes the two sides walk different
 * distances from the identical inputs. This module is the single copy.
 *
 * Rendering is a different clock. The client draws at whatever rate the browser
 * gives it and the server is at the mercy of the event loop, so neither ever
 * gets a run of exact 50 ms frames. `accumulateSteps` is the bridge: it banks
 * real elapsed time and hands back only *whole* steps, so the sim itself never
 * sees a variable `dt` and a replay of the same inputs reproduces bit for bit.
 *
 * Nothing here reads a clock. Elapsed time arrives as an argument — from the
 * `Clock` port, from Colyseus, from a test — because a module that called
 * `Date.now()` could not be replayed at all.
 */
/** Simulation steps per second. The number the whole sim is written against. */
export const TICK_RATE_HZ = 20;
/**
 * Milliseconds in one step.
 *
 * 50 exactly. That is not a coincidence to be relied on quietly: 1000/20 is a
 * whole number, so step boundaries land on exact doubles and the accumulator
 * carries no drift of its own — the only rounding in the loop is whatever the
 * caller's own frame timings brought with them.
 */
export const TICK_MS = 1000 / TICK_RATE_HZ;
/**
 * The most steps `accumulateSteps` will hand back for a single frame.
 *
 * Five steps — 250 ms of simulation — chosen against the two stalls that
 * actually happen:
 *
 *   - A GC pause or a dropped server frame costs tens of milliseconds. 250 ms
 *     absorbs those whole, so the cap never fires during normal play and normal
 *     play is therefore never silently slowed down by it.
 *   - A backgrounded tab is throttled to about 1 Hz, and a minimised one stops
 *     entirely. Uncapped, the first frame back would ask for 20 steps, or 1200
 *     after a minute, and the loop would sit there simulating a minute of game
 *     while the frame it owes goes unrendered — the spiral of death, where each
 *     catch-up is slower than the stall it is repaying.
 *
 * Past a quarter second behind, catching up is the wrong answer anyway: the
 * client is further out than prediction can hide, and the server's next
 * authoritative state is the correction. Better to arrive late and reconcile
 * than to freeze the process replaying a stall.
 */
export const MAX_CATCHUP_STEPS = 5;
/** The same ceiling in milliseconds, for callers that reason in time. */
export const MAX_CATCHUP_MS = MAX_CATCHUP_STEPS * TICK_MS;
const assertMilliseconds = (name, value) => {
    // Phrased as a negated comparison so NaN — a subtraction of two clock reads
    // that were never numbers — is rejected along with the negatives.
    if (!(Number.isFinite(value) && value >= 0)) {
        throw new RangeError(`accumulateSteps expects a finite, non-negative ${name} in milliseconds, received ${value}`);
    }
};
/**
 * Banks `elapsedMs` on top of last frame's `remainder` and splits the total into
 * whole steps plus a new remainder.
 *
 * Pure, and the standard fixed-timestep accumulator: the caller runs `steps`
 * identical steps and carries `remainder` into the next call, so the simulation
 * advances in exact 50 ms units however jittery the frames feeding it are.
 *
 * ```ts
 * const budget = accumulateSteps(carried, now - lastFrame);
 * for (let step = 0; step < budget.steps; step += 1) advance(world);
 * carried = budget.remainder;
 * ```
 *
 * The step count is capped at `MAX_CATCHUP_STEPS` (see there for why) and the
 * surplus is **discarded**, not carried: banking it would leave a debt every
 * later frame is also too small to repay, pinning the loop at the cap forever.
 * The sub-step phase survives the cap, so a capped frame does not also re-align
 * the clock to a step boundary.
 *
 * Time that ran backwards or arrived as NaN is a broken clock — a programmer
 * error, not a game outcome — and throws.
 */
export const accumulateSteps = (remainder, elapsedMs) => {
    assertMilliseconds('remainder', remainder);
    assertMilliseconds('elapsed time', elapsedMs);
    const banked = remainder + elapsedMs;
    const wanted = Math.floor(banked / TICK_MS);
    return {
        steps: Math.min(wanted, MAX_CATCHUP_STEPS),
        remainder: banked - wanted * TICK_MS,
    };
};
//# sourceMappingURL=tick.js.map